import { useEffect, useRef, useState } from 'react'
import { Search, Loader2, Link2 } from 'lucide-react'
import { apiGet } from '../../lib/api.js'
import { fmtMoney } from '../../lib/asset.js'
import { useTypeahead } from '../../lib/useTypeahead.js'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 transition'

/* Live expense search → pick one (or clear). Shared by AssetEditor (purchase
 * link) and AssetMaintenance (repair-cost link). Queries /api/expense?search=. */
export default function ExpensePicker({ value, display, onPick, onClear, placeholder = 'ค้นหาจากชื่อร้าน หรือรายละเอียดใบซื้อ…' }) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const ref = useRef(null)

  // Only the real rows are indexable — loading / empty states are not selectable.
  const ta = useTypeahead({
    count: open && q.trim() && !loading ? rows.length : 0,
    onSelect: (i) => { const e = rows[i]; if (e) { onPick(e); setOpen(false); setQ('') } },
    onClose: () => setOpen(false),
  })

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  useEffect(() => {
    if (!q.trim()) { setRows([]); return }
    let alive = true
    setLoading(true)
    const t = setTimeout(() => {
      apiGet('/api/expense', { search: q.trim(), per_page: 10 })
        .then((r) => { if (alive) setRows(Array.isArray(r) ? r : (r?.items || [])) })
        .catch(() => { if (alive) setRows([]) })
        .finally(() => { if (alive) setLoading(false) })
    }, 300)
    return () => { alive = false; clearTimeout(t) }
  }, [q])

  if (value > 0) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-xl border border-brand-200 bg-brand-50/60 px-3 py-2">
        <span className="min-w-0 flex items-center gap-2 text-[13px] text-brand-700">
          <Link2 size={14} className="shrink-0" />
          <span className="truncate">{display || `ใบซื้อ #${value}`}</span>
        </span>
        <button type="button" onClick={onClear} className="text-[12px] text-slate-400 hover:text-rose-600 shrink-0">ยกเลิกการผูก</button>
      </div>
    )
  }

  return (
    <div className="relative" ref={ref}>
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={ta.onKeyDown}
        placeholder={placeholder}
        className={`${FIELD} pl-9`}
      />
      {/* Search matches the expense `detail` OR the seller name (Xano GET /expense),
          so typing a shop name or a description both find the purchase. */}
      {open && q.trim() && (
        <div ref={ta.listRef} className="absolute z-30 mt-1.5 w-full max-h-60 overflow-auto rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 py-1">
          {loading && <div className="px-3 py-2 text-[13px] text-slate-400"><Loader2 size={13} className="inline animate-spin mr-1.5" /> กำลังค้นหา…</div>}
          {!loading && !rows.length && <div className="px-3 py-2 text-[13px] text-slate-400">ไม่พบใบซื้อที่ตรงกัน — ลองพิมพ์ชื่อร้าน หรือคำที่อยู่ในรายละเอียดของใบนั้น</div>}
          {!loading && rows.map((e, i) => {
            const label = e.detail || '(ไม่มีรายละเอียด)'
            const seller = e?.seller?.seller_name || ''
            const amt = e?.amount?.payment ?? e?.amount?.total_price
            // Sub-line leads with the seller (what people search by), then id + date.
            const sub = [seller, `#${e.id}`, e.date_buy].filter(Boolean).join(' · ')
            return (
              <button
                key={e.id}
                type="button"
                data-ti-index={i}
                onMouseEnter={() => ta.setActiveIndex(i)}
                onMouseDown={(ev) => { ev.preventDefault(); onPick(e); setOpen(false); setQ('') }}
                className={`flex w-full items-center justify-between gap-2 text-left px-3 py-2 transition ${
                  i === ta.activeIndex ? 'bg-brand-50' : 'hover:bg-brand-50'}`}
              >
                <span className="min-w-0">
                  <span className="block text-[13.5px] text-slate-700 truncate">{label}</span>
                  <span className="block text-[11px] text-slate-400 truncate">{sub}</span>
                </span>
                {amt != null && <span className="text-[12px] text-slate-500 tabular-nums shrink-0">{fmtMoney(amt)}฿</span>}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
